// HelpInfoSystem.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    InputAdornment,
    IconButton,
    Tooltip,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider,
    Paper
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import { useUser } from '../components/userContext';
import { useThemeContext, THEME_CLASSIC, THEME_DARK } from '../theme/ThemeContext';
import { usePermissions } from '../components/permissionContext';
import { useConfig } from '../components/configContext';
import {
    DrkDialog,
    DrkDialogCard,
    DrkInfoBox,
    DrkFormField,
    DrkHeaderIconButton
} from '../components/DrkComponents';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * HelpIconButton - Button mit Info-Icon, der den Hilfe-Dialog öffnet
 */
export const HelpIconButton = ({
    pageId,
    position = 'topRight',
    size = 'medium',
    inHeader = false, // neuer Prop für die Verwendung im Header
    onColoredBackground, // true = farbiger Hintergrund (z.B. DrkDialog), false = heller Hintergrund (z.B. PlanningHeader)
    ...props
}) => {
    const [openHelpDialog, setOpenHelpDialog] = useState(false);
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const isDark = currentTheme === THEME_DARK;
    const { hasFeature } = usePermissions();

    const positionStyles = {
        topRight: { position: 'absolute', top: 16, right: 16 },
        topLeft: { position: 'absolute', top: 16, left: 16 },
        bottomRight: { position: 'absolute', bottom: 16, right: 16 },
        bottomLeft: { position: 'absolute', bottom: 16, left: 16 },
        inline: { display: 'inline-flex', marginLeft: 8 }
    };

    const iconSize = {
        small: { size: 'small', iconSize: 18 },
        medium: { size: 'medium', iconSize: 24 },
        large: { size: 'large', iconSize: 32 }
    };

    // Bedingte Hintergrundfarbe: Nur im modernen Theme und wenn im Header
    const hoverBackgroundColor = (theme) => {
        if (theme.palette.mode === 'dark') {
            return inHeader ? 'rgba(255, 255, 255, 0.3)' : 'rgba(242, 132, 36, 0.3)';
        }
        if (!isClassic && inHeader) return '#fbe9e7';
        if (!isClassic) return 'rgba(244, 155, 141, 0.1)';
        return 'transparent';
    };

    const backgroundColor = (theme) => {
        if (theme.palette.mode === 'dark') {
            return inHeader ? 'rgba(255, 255, 255, 0.2)' : 'rgba(242, 132, 36, 0.2)';
        }
        if (isClassic) return inHeader ? 'rgba(242,245,248,0.72)' : 'rgba(46,117,182,0.25)';
        return inHeader ? '#ffccbc' : 'rgba(228,100,80,0.2)';
    };

    const handleOpenHelp = () => {
        setOpenHelpDialog(true);
    };

    const handleCloseHelp = () => {
        setOpenHelpDialog(false);
    };

    if (!hasFeature('HELP-SYSTEM')) {
        return null;
    }

    // Default: onColoredBackground ist true wenn inHeader (für Rückwärtskompatibilität mit Dialog-Headern)
    const hasColoredBackground = onColoredBackground !== undefined ? onColoredBackground : inHeader;

    // Bestimme die Icon-Farbe basierend auf Theme und Hintergrund
    const getIconColor = () => {
        if (!inHeader) return 'primary';
        // Farbiger Hintergrund (z.B. DrkDialog) → weiß
        if (hasColoredBackground) return '#ffffff';
        // Heller Hintergrund (z.B. PlanningHeader) → theme-abhängig
        return isDark ? '#ffffff' : '#555555';
    };

    // Bestimme die Hintergrundfarbe des Buttons
    const getButtonBgColor = () => {
        if (!inHeader) return undefined;
        // Farbiger Header-Hintergrund (z.B. DrkDialog mit orangem Header)
        if (hasColoredBackground) {
            // Im Dark Mode: dunkler Kreis auf orangem Header, im Light Mode: leicht transparent weiß
            return isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.2)';
        }
        // Heller Header-Hintergrund (z.B. PlanningHeader) → wie Settings-Button
        return isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';
    };

    if (position === 'inline') {
        return (
            <>
                <DrkHeaderIconButton
                    icon={<HelpIcon />}
                    tooltip="Hilfe & Informationen"
                    onClick={handleOpenHelp}
                    size={size}
                    color={getIconColor()}
                    bgColor={getButtonBgColor()}
                    {...props}
                />

                {openHelpDialog && (
                    <HelpInfoDialog
                        open={openHelpDialog}
                        onClose={handleCloseHelp}
                        pageId={pageId}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <Tooltip title="Hilfe & Informationen">
                <IconButton
                    color="primary"
                    onClick={handleOpenHelp}
                    size={iconSize[size].size}
                    sx={{
                        ...positionStyles[position],
                        bgcolor: backgroundColor,
                        '&:hover': {
                            bgcolor: hoverBackgroundColor,
                        },
                        ...(inHeader && {
                            p: 0.4,
                            borderRadius: '50%',
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                        }),
                        ...props.sx
                    }}
                    {...props}
                >
                    <HelpIcon sx={{ fontSize: iconSize[size].iconSize }} />
                </IconButton>
            </Tooltip>

            {openHelpDialog && (
                <HelpInfoDialog
                    open={openHelpDialog}
                    onClose={handleCloseHelp}
                    pageId={pageId}
                />
            )}
        </>
    );
};

/**
 * HelpInfoDialog - Dialog zur Anzeige von Hilfeinformationen
 */
export const HelpInfoDialog = ({ open, onClose, pageId }) => {
    const { user } = useUser();
    const { currentTheme } = useThemeContext();
    const { hasFeature } = usePermissions();
    const { getConfigValue } = useConfig();
    const isClassic = currentTheme === THEME_CLASSIC;
    const [helpInfo, setHelpInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [historyMode, setHistoryMode] = useState(false);
    const [history, setHistory] = useState([]);
    const [editedTitle, setEditedTitle] = useState('');
    const [editedContent, setEditedContent] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    // codi (KI-Assistent) – Minimalintegration
    const [codiVisible, setCodiVisible] = useState(false);
    const [codiQuestion, setCodiQuestion] = useState('');
    const [codiAnswer, setCodiAnswer] = useState('');
    const [codiLoading, setCodiLoading] = useState(false);
    const [codiError, setCodiError] = useState(null);

    // Feature-Flag (global) und UI-Schalter (codi_constants.enabled)
    const hasCodiUser = hasFeature('CODI-USER');
    const codiFeatureOn = getConfigValue('codi_enabled', false);
    const [codiActivated, setCodiActivated] = useState(false);

    // Codi-Konfiguration laden (nur wenn global aktiv und Feature vorhanden)
    useEffect(() => {
        let aborted = false;
        async function loadCodiConfig() {
            try {
                setCodiActivated(false);
                if (!user || !user.api_key) return;
                if (!codiFeatureOn || !hasCodiUser) return;
                const resp = await fetch(`${process.env.REACT_APP_API_URI}/api/codi/config`, {
                    method: 'GET',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                });
                if (!resp.ok) return;
                const data = await resp.json();
                if (!aborted) setCodiActivated(!!data?.data?.enabled);
            } catch (e) {
                if (!aborted) setCodiActivated(false);
            }
        }
        loadCodiConfig();
        return () => { aborted = true; };
    }, [user, codiFeatureOn, hasCodiUser]);

    // Sichtbarkeit zurücksetzen, falls Flags entfallen
    useEffect(() => {
        if (!(hasCodiUser && codiFeatureOn && codiActivated) && codiVisible) {
            setCodiVisible(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasCodiUser, codiFeatureOn, codiActivated]);

    // Prüfen, ob der Benutzer Admin-Rechte hat
    const isAdmin = Boolean(user?.role === 'admin' || hasFeature('HELP-SYSTEM-ADMIN'));

    // Hilfeinformationen laden
    const fetchHelpInfo = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${process.env.REACT_APP_API_URI}/api/page-help/${pageId}`, {
                method: 'GET',
                headers: {
                    'Authorization': user.api_key,
                },
            });

            if (response.status === 404) {
                // Wenn keine Informationen vorhanden sind, leeren State setzen
                setHelpInfo(null);
                setLoading(false);
                return;
            }

            if (!response.ok) {
                throw new Error('Fehler beim Laden der Hilfeinformationen');
            }

            const data = await response.json();
            setHelpInfo(data.data);
            setEditedTitle(data.data.title || '');
            setEditedContent(data.data.content || '');
        } catch (err) {
            console.error('Fehler beim Laden der Hilfeinformationen:', err);
            setError('Fehler beim Laden der Hilfeinformationen');
        } finally {
            setLoading(false);
        }
    };

    // Versionshistorie laden
    const fetchHistory = async () => {
        if (!helpInfo?.help_id) return;

        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${process.env.REACT_APP_API_URI}/api/page-help/${helpInfo.help_id}/history`, {
                method: 'GET',
                headers: {
                    'Authorization': user.api_key,
                },
            });

            if (!response.ok) {
                throw new Error('Fehler beim Laden der Versionshistorie');
            }

            const data = await response.json();
            setHistory(data.data);
        } catch (err) {
            console.error('Fehler beim Laden der Versionshistorie:', err);
            setError('Fehler beim Laden der Versionshistorie');
        } finally {
            setLoading(false);
        }
    };

    // Spezifische Version laden
    const fetchHistoryVersion = async (historyId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${process.env.REACT_APP_API_URI}/api/page-help/history/${historyId}`, {
                method: 'GET',
                headers: {
                    'Authorization': user.api_key,
                },
            });

            if (!response.ok) {
                throw new Error('Fehler beim Laden der Version');
            }

            const data = await response.json();
            return data.data;
        } catch (err) {
            console.error('Fehler beim Laden der Version:', err);
            setError('Fehler beim Laden der Version');
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Hilfeinformationen speichern
    const saveHelpInfo = async () => {
        try {
            setSaving(true);
            setSaveError(null);

            const response = await fetch(`${process.env.REACT_APP_API_URI}/api/page-help`, {
                method: 'POST',
                headers: {
                    'Authorization': user.api_key,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    page_id: pageId,
                    title: editedTitle,
                    content: editedContent
                }),
            });

            if (!response.ok) {
                throw new Error('Fehler beim Speichern der Hilfeinformationen');
            }

            // Neu laden und in den Anzeigemodus wechseln
            await fetchHelpInfo();
            setEditMode(false);
        } catch (err) {
            console.error('Fehler beim Speichern der Hilfeinformationen:', err);
            setSaveError('Fehler beim Speichern der Hilfeinformationen');
        } finally {
            setSaving(false);
        }
    };

    // Beim ersten Öffnen laden
    useEffect(() => {
        if (open && user) {
            fetchHelpInfo();
        }
        // eslint-disable-next-line
    }, [open, pageId, user]);

    // Bei Wechsel in den Verlauf-Modus die Historie laden
    useEffect(() => {
        if (historyMode && helpInfo?.help_id) {
            fetchHistory();
        }
        // eslint-disable-next-line
    }, [historyMode, helpInfo]);

    // Handler für den Bearbeiten-Button
    const handleEditClick = () => {
        setHistoryMode(false);
        setEditMode(true);
    };

    // Handler für den Verlauf-Button
    const handleHistoryClick = () => {
        setEditMode(false);
        setHistoryMode(true);
    };

    // Handler: Frage an codi senden
    const askCodi = async () => {
        try {
            setCodiLoading(true);
            setCodiError(null);
            setCodiAnswer('');

            const prompt = (codiQuestion && codiQuestion.trim()) || 'Bitte erkläre diese Seite kurz und nenne die wichtigsten Aktionen.';
            const response = await fetch(`${process.env.REACT_APP_API_URI}/api/codi/chat`, {
                method: 'POST',
                headers: {
                    'Authorization': user.api_key,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    page_id: pageId,
                    messages: [{ role: 'user', content: prompt }]
                })
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err?.message || 'Fehler beim Abrufen der codi-Antwort');
            }

            const data = await response.json();
            setCodiAnswer(data?.data?.reply || '');
        } catch (e) {
            console.error('codi error:', e);
            setCodiError(e.message || 'Unbekannter Fehler');
        } finally {
            setCodiLoading(false);
        }
    };

    // Handler für das Wiederherstellen einer Version
    const handleRestoreVersion = async (historyId) => {
        const versionData = await fetchHistoryVersion(historyId);
        if (versionData) {
            setEditedTitle(versionData.title || '');
            setEditedContent(versionData.content || '');
            setEditMode(true);
            setHistoryMode(false);
        }
    };

    // Dialoge für verschiedene Modi
    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <DrkInfoBox color="error" sx={{ my: 2 }}>
                    <Typography color="error">{error}</Typography>
                </DrkInfoBox>
            );
        }

        if (editMode) {
            // Bearbeitungsmodus
            return (
                <>
                    {saveError && (
                        <DrkInfoBox color="error" sx={{ mb: 2 }}>
                            <Typography color="error">{saveError}</Typography>
                        </DrkInfoBox>
                    )}

                    <DrkFormField
                        label="Titel"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        fullWidth
                        required
                        margin="normal"
                    />

                    <DrkFormField
                        label="Inhalt"
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        multiline
                        rows={10}
                        fullWidth
                        margin="normal"
                        placeholder="Markdown-Formatierung wird unterstützt"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Markdown wird unterstützt">
                                        <InfoIcon color="action" />
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <DrkInfoBox sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Formatierungshilfe (Markdown)
                        </Typography>
                        <Typography variant="body2">
                            <b># Überschrift 1</b> | <b>## Überschrift 2</b> | <b>**Fett**</b> | <b>*Kursiv*</b> | <b>[Link](URL)</b>
                        </Typography>
                        <Typography variant="body2">
                            <b>- Listenpunkt</b> | <b>1. Nummerierte Liste</b> | <b>```Code```</b> | <b> Zitat</b>
                        </Typography>
                    </DrkInfoBox>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => {
                                setEditMode(false);
                                // Zurücksetzen der Eingabefelder auf den aktuellen Stand
                                if (helpInfo) {
                                    setEditedTitle(helpInfo.title || '');
                                    setEditedContent(helpInfo.content || '');
                                }
                            }}
                            disabled={saving}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={saveHelpInfo}
                            disabled={!editedTitle.trim() || saving}
                            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        >
                            Speichern
                        </Button>
                    </Box>
                </>
            );
        }

        if (historyMode) {
            // Verlauf-Ansicht
            return (
                <>
                    <Typography variant="h6" gutterBottom>
                        Versionshistorie
                    </Typography>

                    {history.length === 0 ? (
                        <DrkInfoBox sx={{ my: 2 }}>
                            <Typography>Keine Versionshistorie verfügbar.</Typography>
                        </DrkInfoBox>
                    ) : (
                        <List>
                            {history.map((version, index) => (
                                <React.Fragment key={version.history_id}>
                                    <ListItem
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="restore"
                                                onClick={() => handleRestoreVersion(version.history_id)}
                                                title="Diese Version wiederherstellen"
                                            >
                                                <RestoreIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={`Version ${version.version}: ${version.title}`}
                                            secondary={`Geändert am ${new Date(version.created_at).toLocaleDateString()} von ${version.modified_by}`}
                                        />
                                    </ListItem>
                                    {index < history.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => setHistoryMode(false)}
                        >
                            Zurück
                        </Button>
                    </Box>
                </>
            );
        }

        // Standardanzeige
        if (!helpInfo) {
            return (
                <DrkInfoBox sx={{ my: 2 }}>
                    <Typography>
                        Für diese Seite sind noch keine Hilfeinformationen verfügbar.
                        {isAdmin && ' Klicken Sie auf "Bearbeiten", um Hilfeinformationen hinzuzufügen.'}
                    </Typography>
                </DrkInfoBox>
            );
        }

        return (
            <>
                {codiVisible && (
                    <Paper
                        elevation={0}
                        sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : (isClassic ? 'transparent' : '#f3f6f9'), border: isClassic ? '1px solid #e0e0e0' : 'none' }}
                    >
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Frage an codi</Typography>
                        <DrkFormField
                            label="Deine Frage"
                            value={codiQuestion}
                            onChange={(e) => setCodiQuestion(e.target.value)}
                            fullWidth
                            margin="dense"
                            placeholder="z.B. Was kann ich hier tun?"
                        />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={askCodi}
                                disabled={codiLoading}
                                startIcon={codiLoading ? <CircularProgress size={18} /> : undefined}
                            >
                                Senden
                            </Button>
                            {codiError && (
                                <Typography color="error" variant="body2">{codiError}</Typography>
                            )}
                        </Box>
                        {codiAnswer && (
                            <Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : (isClassic ? 'transparent' : '#ffffff'), border: isClassic ? '1px solid #e0e0e0' : 'none' }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {codiAnswer}
                                </ReactMarkdown>
                            </Paper>
                        )}
                    </Paper>
                )}
                <Box sx={{ mb: 2 }}>
                    <Chip
                        size="small"
                        label={`Version ${helpInfo.version}`}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        Zuletzt aktualisiert am {new Date(helpInfo.modified_at).toLocaleDateString()}
                        {helpInfo.last_modified_by && ` von ${helpInfo.last_modified_by}`}
                    </Typography>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        pl: 2,
                        pr: 2,
                        pt: 0,
                        pb: 2,
                        borderRadius: 2,
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : (isClassic ? 'transparent' : '#f9fafb'),
                        border: isClassic ? '1px solid #e0e0e0' : 'none'
                    }}
                >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {helpInfo.content}
                    </ReactMarkdown>
                </Paper>
            </>
        );
    };

    // Dialog-Aktionen basierend auf dem Modus
    const dialogActions = (
        <>
            {hasCodiUser && codiFeatureOn && codiActivated && (
                <Button
                    onClick={() => setCodiVisible(v => !v)}
                    color="primary"
                >
                    {codiVisible ? 'codi ausblenden' : 'Frag codi'}
                </Button>
            )}
            {!editMode && !historyMode && helpInfo && helpInfo.help_id && isAdmin && (
                <Button
                    startIcon={<HistoryIcon />}
                    onClick={handleHistoryClick}
                    color="inherit"
                >
                    Verlauf
                </Button>
            )}

            {!editMode && !historyMode && isAdmin && (
                <Button
                    startIcon={<EditIcon />}
                    onClick={handleEditClick}
                    color="primary"
                    variant="contained"
                >
                    {helpInfo ? 'Bearbeiten' : 'Erstellen'}
                </Button>
            )}

            <Button color="inherit" onClick={onClose}>
                Schließen
            </Button>
        </>
    );

    return (
        <DrkDialog
            open={open}
            onClose={onClose}
            title={editMode
                ? (helpInfo ? 'Hilfeinformation bearbeiten' : 'Neue Hilfeinformation erstellen')
                : (historyMode
                    ? 'Versionshistorie'
                    : (helpInfo?.title || 'Hilfe & Informationen'))}
            maxWidth="md"
            fullWidth
            actions={dialogActions}
        >
            <DrkDialogCard>
                {renderContent()}
            </DrkDialogCard>
        </DrkDialog>
    );
};


const defaultExport = { HelpIconButton, HelpInfoDialog };
export default defaultExport;
